/**
 * Report Prompts Configuration
 * 
 * Configuration for editable prompts and available LLM models
 * 
 * NOTE: These are fallback/default values. The actual configuration
 * should be loaded from the backend API using loadReportConfig().
 */

// Fallback/default values (used if API fails or before API loads)
export const EDITABLE_PROMPTS = [
  {
    key: 'BRIEF_SUMMARY_PROMPT',
    name: 'Brief Summary',
    description: 'Creates a brief summary of the discussion (12-15 sentences, 200-250 words)',
    requiredPlaceholders: ['{TRANSCRIPT}']
  },
  {
    key: 'MAIN_ARGS_PROMPT',
    name: 'Main Arguments',
    description: 'Identifies main arguments presented along with major discussion points',
    requiredPlaceholders: ['{TRANSCRIPT}', '{SPEAKERS}']
  },
  {
    key: 'JSON_MAIN_ARGS_DESC_PROMPT',
    name: 'Arguments Details',
    description: 'Detailed analysis of arguments with speakers, explanations, and evidence',
    requiredPlaceholders: ['{TRANSCRIPT}', '{SPEAKERS}', '{ARGUMENTS}', '{TOPICS}']
  },
  {
    key: 'JSON_KEY_AGREEMENT_PROMPT',
    name: 'Agreements',
    description: 'Identifies key agreement points among speakers',
    requiredPlaceholders: ['{TRANSCRIPT}', '{ARGUMENTS}', '{TOPICS}']
  },
  {
    key: 'JSON_KEY_DISAGREEMENT_PROMPT',
    name: 'Disagreements',
    description: 'Identifies key disagreement points among speakers',
    requiredPlaceholders: ['{TRANSCRIPT}', '{ARGUMENTS}', '{TOPICS}']
  },
  {
    key: 'JSON_CONCLUSIONS_PROMPT',
    name: 'Conclusions',
    description: 'Identifies key takeaways, resolutions, and unresolved issues',
    requiredPlaceholders: ['{TRANSCRIPT}', '{MAJOR_KEYPOINTS}']
  },
  {
    key: 'REPORT_PROMPT',
    name: 'Full Report',
    description: 'Expands brief summary into detailed comprehensive report',
    requiredPlaceholders: [
      '{TRANSCRIPT}',
      '{BRIEF_SUMMARY}',
      '{MAJOR_KEYPOINTS}',
      '{ARGUMENTS}',
      '{AGREEMENTS}',
      '{DISAGREEMENTS}',
      '{TAKEAWAYS}',
      '{THOUGHT_PROVOKING_COMMENTS}',
      '{FOLLOW_UP_QUESTIONS}'
    ]
  }
];

// Fallback/default values
export const AVAILABLE_MODELS = [
  {
    value: 'openai/gpt-oss-120b',
    label: 'GPT-OSS 120B (Local)',
    provider: 'ChatGPT-OSS',
    description: 'Local 120B model on chatgpt-oss.diplomacy.edu - tries first, then backend can fall back to GPT-5.4',
    isDefault: true
  },
  // Anthropic/Claude is disabled for report generation selection.
  // Keep this block here as a quick rollback point if we re-enable it later.
  // {
  //   value: 'claude-sonnet-4-6',
  //   label: 'Claude Sonnet 4.6',
  //   provider: 'Anthropic',
  //   description: 'High-quality report generation via Anthropic API',
  //   isDefault: false
  // },
  {
    value: 'gpt-5.4',
    label: 'GPT-5.4',
    provider: 'OpenAI',
    description: 'Fallback/default external model for report generation via OpenAI API',
    isDefault: false
  },
  {
    value: 'gpt-4o',
    label: 'GPT-4o',
    provider: 'OpenAI',
    description: 'Alternative model for report generation',
    isDefault: false
  }
];

/**
 * Get default model value
 */
export const getDefaultModel = () => {
  const defaultModel = AVAILABLE_MODELS.find(m => m.isDefault);
  return defaultModel ? defaultModel.value : AVAILABLE_MODELS[0].value;
};

/**
 * Default prompts - FALLBACK ONLY
 * These are only used if API fails to load configuration.
 * In normal operation, default prompts are loaded from backend API.
 */
export const DEFAULT_PROMPTS = {
  BRIEF_SUMMARY_PROMPT: `You will be given a transcript of a discussion. Your task is to create a brief summary of this discussion. Here is the transcript:

<transcript>
{TRANSCRIPT}
</transcript>

To summarize this discussion effectively, please follow these guidelines:

1. Read through the entire transcript carefully to understand the main topic and key points discussed.
2. Identify the central theme or focus of the discussion.
3. Note the most important arguments, ideas, or conclusions presented by the participants.
4. Pay attention to any significant agreements or disagreements among the participants.
5. Observe any notable examples or evidence used to support arguments.
6. Your summary should be 12-15 sentences long. This is approximately 200-250 words.
7. Write the summary as a cohesive text, not in bullet points. Use proper paragraph structure.
8. Begin with a sentence that introduces the main topic of the discussion.
9. Organize the summary in a logical flow, connecting ideas and points smoothly.
10. Include the key elements of the discussion: main arguments, important details, and any conclusions reached.
11. Use a neutral, objective tone. Avoid inserting personal opinions or evaluations of the discussion.
12. Write in clear, concise language. Avoid jargon unless it's essential to understanding the discussion's content.
13. Conclude with a sentence that encapsulates the overall outcome or significance of the discussion.

Please write your summary within <content> tags. Remember to focus on providing a concise yet comprehensive overview of the discussion in a flowing, readable text format.`,

  MAIN_ARGS_PROMPT: `You are tasked with analyzing a transcript of a discussion and identifying the main arguments presented, along with the major discussion points they belong to. Follow these steps carefully:

1. Read the following transcript of a discussion:

<transcript>
{TRANSCRIPT}
</transcript>

<speakers names>
{SPEAKERS}
</speakers names>

2. Analyze the transcript thoroughly, paying attention to the flow of the conversation and the key points being made by the participants.
3. Identify the major discussion points for each speaker. These are the overarching topics or themes that the conversation revolves around. There may be several major discussion points in a single transcript.
4. For each major discussion point, identify the main arguments presented. Main arguments are key statements or claims that support or relate to the major discussion point. They should be substantial points that contribute significantly to the discussion, not minor details or tangential comments. Identify some important argument for each speaker from the speakers names list. Do not include arguments that are not presented in the transcript. Do not leave any speaker without argument.
5. Organize your findings in a structured format. For each major discussion point, list the main arguments that belong to it. Use clear and concise language to summarize each argument.
6. Present your analysis in the following format:
   - Major discussion point 1
     - Main argument 1 - short name (name of the argument speaker, as presented in speakers names list)
     - Main argument 2 - short name (name of the argument speaker, as presented in speakers names list)
     (etc.)
   - Major discussion point 2
     - Main argument 1 - short name (name of the argument speaker, as presented in speakers names list)
     - Main argument 2 - short name (name of the argument speaker, as presented in speakers names list)
     (etc.)

7. Ensure that your summary captures the essence of the discussion without including unnecessary details. Focus on the most important and relevant arguments for each discussion point.
8. Write your final output within <content> tags. Do not include any other text or explanations outside of these tags.

Remember to be objective in your analysis, accurately representing the arguments presented in the discussion without adding your own opinions or interpretations.`,

  JSON_MAIN_ARGS_DESC_PROMPT: `You are tasked with analyzing a transcript and a list of arguments from a discussion or debate. Your goal is to identify the speaker for each main argument, explain it concisely, and note any supporting evidence or examples provided.

Here is the transcript of the discussion:
<transcript>
{TRANSCRIPT}
</transcript>

<speakers names>
{SPEAKERS}
</speakers names>

Here is the list of main arguments:
<arguments>
{ARGUMENTS}
</arguments>

<topics>
{TOPICS}
</topics>

The available topics for classification are:
{TOPICS_LIST}

For each argument in the list, please follow these steps:
1. Identify the speaker who made the argument, as presented in speakers names list.
2. Explain the argument in 2-3 sentences.
3. Note any supporting evidence or examples provided for the argument.
4. Identify to which of the provided topics the argument belongs to. You may select one or more topics that are relevant to the argument. Use the exact topic names as listed above. Format your response as: "Topic1 | Topic2 | Topic3" (separate multiple topics with " | ").

4. Present your analysis in the following format:
[
   {{
      "Speaker": "Speaker 1",
      "Arguments": [
         {{
            "Argument": "Main argument 1",
            "Explanation": "Explain the argument in 2-3 sentences.",
            "Evidence": "Supporting evidence or examples provided",
            "Major discussion point": "Major discussion point",
            "Topics": "Topic1 | Topic2 | Topic3"
         }},
         {{
            "Argument": "Main argument 2",
            "Explanation": "Explain the argument in 2-3 sentences.",
            "Evidence": "Supporting evidence or examples provided",
            "Major discussion point": "Major discussion point",
            "Topics": "Topic1 | Topic2 | Topic3"
         }},
         ...
         ]
   }},
   {{
      "Speaker": "Speaker 2",
      "Arguments": [...]}},
   ...
]

Your job depends on correct identifying speaker of the argument, do your best to always successfuly do that.

If no supporting evidence or examples are provided for an argument, write "".

Analyze each argument in the order they are presented in the <arguments> list. Be objective and accurate in your analysis, focusing solely on the information provided in the transcript and argument list.
Write your final output within <content> tags. Do not include any other text or explanations outside of these tags.
IMPORTANT: Make sure that all arguments from the <arguments> list are included in your JSON output, even if they have no supporting evidence or examples. Make sure that you don't change text of the arguments, you should always use the same text as in the <arguments> list.`,

  JSON_KEY_AGREEMENT_PROMPT: `You are tasked with analyzing a transcript and a list of arguments from a discussion or debate.

Here is the transcript of the discussion:
<transcript>
{TRANSCRIPT}
</transcript>

Here is the list of main arguments:
<arguments>
{ARGUMENTS}
</arguments>

<topics>
{TOPICS}
</topics>

The available topics for classification are:
{TOPICS_LIST}

1. Identify key agreement discussion points among the speakers. 
2. Identify which specific speakers shared similar viewpoints or arguments, and on what particular issues
3. Identify if there were there any unexpected areas of consensus.
4. Provide overall assessment of the level of consensus among the speakers and its implications for the topic at hand.
5. Identify to which of the provided topics each agreement point belongs to. You may select one or more topics that are relevant. Use the exact topic names as listed above. Format your response as: "Topic1 | Topic2 | Topic3" (separate multiple topics with " | ").

After your analysis, provide your findings in the following JSON format:

<content>
{{
   "Agreements":
   [
      {{"Agreement point": "Point 1", "Speakers": ["Speaker A", "Speaker B", ...], "Arguments": ["Argument 1", "Argument 2", ...], "Summary": "Summary of their shared viewpoint", "Topics": "Topic1 | Topic2 | Topic3"}},
      ...
   ],
   "Similar viewpoints":
   [
      {{"Speakers": ["Speaker A", "Speaker B", ...], "Arguments": ["Argument 1", "Argument 2", ...], "Shared viewpoint": "Explanation of shared viewpoint", "Topics": "Topic1 | Topic2 | Topic3"}},
      ...
   ],
   "Unexpected consensus":
   [
      {{"Issue": "Issue 1", "Speakers": ["Speaker A", "Speaker B", ...], "Arguments": ["Argument 1", "Argument 2", ...], "Explanation": "Explanation of unexpected consensus and its significance", "Topics": "Topic1 | Topic2 | Topic3"}},
      ...
   ],
   "Overall assessment":
   [
      {{"Summary": "Brief summary of main areas of agreement", "Consensus level": "Level of consensus and its implications"}},
      ...
   ]   
   
}}
</content>

Ensure that your analysis is thorough, objective, and based solely on the information provided in the transcript and arguments details. Do not introduce external information or make assumptions beyond what is explicitly stated or strongly implied in the given text.
Make sure that you don't change text of the arguments, you should always use the same text as in the <arguments> list.`,

  JSON_KEY_DISAGREEMENT_PROMPT: `You are tasked with analyzing a transcript and a list of arguments from a discussion or debate.

Here is the transcript of the discussion:
<transcript>
{TRANSCRIPT}
</transcript>

Here is the list of main arguments:
<arguments>
{ARGUMENTS}
</arguments>

<topics>
{TOPICS}
</topics>

The available topics for classification are:
{TOPICS_LIST}

1. Identify key disagreement discussion points among the speakers. 
2. Identify specific points where speakers agreed on the same goal but disagreed on how to reach it
3. Identify if there were there any unexpected areas of disagreement.
4 Provide overall assessment of the level of disagreement among the speakers and its implications for the topic at hand.
5. Identify to which of the provided topics each disagreement point belongs to. You may select one or more topics that are relevant. Use the exact topic names as listed above. Format your response as: "Topic1 | Topic2 | Topic3" (separate multiple topics with " | ").

After your analysis, provide your findings in the following JSON format:

<content>
{{
   "Disagreements":
   [
      {{"Disagreement point": "Point 1", "Speakers": ["Speaker A", "Speaker B", ...], "Arguments": ["Argument 1", "Argument 2", ...], "Summary": "Summary of their different viewpoints", "Topics": "Topic1 | Topic2 | Topic3"}},
      ...
   ],
   "Partial agreements":
   [
      {{"Speakers": ["Speaker A", "Speaker B", ...], "Arguments": ["Argument 1", "Argument 2", ...], "Partial agreement": "Explanation of points where speakers agreed on the same goal but disagreed on how to reach it", "Topics": "Topic1 | Topic2 | Topic3"}},
      ...
   ],
   "Unexpected disagreements":
   [
      {{"Issue": "Issue 1", "Speakers": ["Speaker A", "Speaker B", ...], "Arguments": ["Argument 1", "Argument 2", ...], "Explanation": "Explanation of unexpected disagreement and its significance", "Topics": "Topic1 | Topic2 | Topic3"}},
      ...
   ],
   "Overall assessment":
   [
      {{"Summary": "Brief summary of main areas of disagreement", "Disagreement level": "Level of disagreement and its implications"}},
      ...
   ]   
   
}}
</content>

Ensure that your analysis is thorough, objective, and based solely on the information provided in the transcript and arguments details. Do not introduce external information or make assumptions beyond what is explicitly stated or strongly implied in the given text.
Make sure that you don't change text of the arguments, you should always use the same text as in the <arguments> list.`,

  JSON_CONCLUSIONS_PROMPT: `You will be analyzing a discussion transcript to identify key takeaways or conclusions from the discussion. Here is the transcript and major keypoints of the discussion:

<transcript>
{TRANSCRIPT}
</transcript>

<major keypoints>
{MAJOR_KEYPOINTS}
</major keypoints>

Carefully read and analyze the provided transcript. Your task is to summarize the discussion and highlight important elements. Follow these steps:

1. Identify the key takeaways or conclusions from the discussion.

2. Determine if any resolutions were reached or action items proposed. Were there any decisions made or specific tasks assigned to participants?

3. Identify any questions or issues that remain unresolved. What topics or concerns were raised but not fully addressed?

4. Note any compromises that were suggested during the discussion.

Provide your analysis in a structured format using the following tags:

<content>
{{"Key takeaways": [List the main takeaways or conclusions from the discussion],
   "Resolutions and action items": [Python list any resolutions reached or action items proposed],
   "Unresolved issues": [Python list any questions or issues that remain unresolved],
   "Suggested compromises": [Python list any compromises that were suggested during the discussion]
}}
</content>

If any of these sections do not apply to the discussion, simply state "None identified" within the relevant tags.

Ensure that your analysis is objective and based solely on the information provided in the transcript and major keypoints. Do not include personal opinions or information from outside sources.`,

  REPORT_PROMPT: `You are tasked with expanding a brief summary of a transcript into a more detailed and comprehensive summary. You will be provided with the original transcript, a brief summary and several additional elements to incorporate into your expanded summary.

First, review the following information:

<transcript>
{TRANSCRIPT}
</transcript>

<brief_summary>
{BRIEF_SUMMARY}
</brief_summary>

Your task is to create an expanded summary that provides a more in-depth analysis of the content. This extended summary should include more details about the main points, arguments, and evidence presented in the transcript, as well as the conclusion.

To assist you in creating an expanded summary, consider the following additional information:

<major_keypoints>
{MAJOR_KEYPOINTS}
</major_keypoints>

<arguments>
{ARGUMENTS}
</arguments>

<agreements>
{AGREEMENTS}
</agreements>

<disagreements>
{DISAGREEMENTS}
</disagreements>

<takeaways>
{TAKEAWAYS}
</takeaways>

<thought_provoking_comments>
{THOUGHT_PROVOKING_COMMENTS}
</thought_proVOKING_COMMENTS>

<follow_up_questions>
{FOLLOW_UP_QUESTIONS}
</follow_up_questions>

When writing the expanded summary:

1. Use clear and concise language
2. Maintain a neutral tone
3. Organize the information logically
4. Use transitional phrases to connect ideas
5. Ensure that the summary flows smoothly from one point to the next

Important: Use UK spelling and grammar throughout your expanded summary.

Please provide your expanded summary within <content> tags.`
};

/**
 * Load report configuration from backend API
 * Falls back to hardcoded values if API call fails
 * 
 * @returns {Promise<Object>} Configuration object with editable_prompts, available_models, default_prompts
 */
export const loadReportConfig = async () => {
  try {
    const { reportApi } = await import('../api/reportApi');
    const config = await reportApi.getReportConfig();
    
    // Transform backend response to match frontend format
    return {
      editablePrompts: config.editable_prompts.map(p => ({
        key: p.key,
        name: p.name,
        description: p.description,
        requiredPlaceholders: p.required_placeholders
      })),
      availableModels: config.available_models.map(m => ({
        value: m.value,
        label: m.label,
        provider: m.provider,
        description: m.description,
        isDefault: m.is_default
      })),
      defaultPrompts: config.default_prompts
    };
  } catch (error) {
    console.warn('Failed to load report config from API, using fallback values:', error);
    // Return fallback values
    return {
      editablePrompts: EDITABLE_PROMPTS,
      availableModels: AVAILABLE_MODELS,
      defaultPrompts: DEFAULT_PROMPTS
    };
  }
};
